/**
 * Icon Utilities
 *
 * Shared helpers for resolving Lucide icon names from the API
 * to their React component counterparts.
 *
 * Uses a curated icon map instead of `import * as LucideIcons` to keep
 * the bundle lean. Only icons actually returned by the API (KeywordOption.icon,
 * OutletToggleOption.icon, and custom link icons) are included. If a new
 * icon name is added to the backend seed data, it must also be added here.
 *
 * Note: Vibes use emoji icons stored in the database (served via ui-metadata),
 * not Lucide icons from this map.
 */

import {
  // Fallback / default
  Sparkles,

  // Beverages & drinks
  Beer,
  Wine,
  Martini,
  Coffee,
  CupSoda,
  GlassWater,
  IceCream,

  // Food & dining
  UtensilsCrossed,
  Utensils,
  ChefHat,
  Salad,
  Leaf,

  // Nature & views
  TreePine,
  Mountain,
  Sun,
  Sunrise,
  Moon,
  Eye,

  // People & social
  Users,
  Baby,
  Dog,
  Heart,

  // Entertainment
  Music,
  Trophy,
  Tv,
  Gamepad2,
  Sofa,

  // Transport & parking
  Car,
  Truck,
  CircleParking,

  // Business & tech
  Briefcase,
  Zap,
  Package,
  CalendarCheck,
  Smartphone,
  Wallet,
  CreditCard,
  Banknote,
  Accessibility,

  // Vibes
  PartyPopper,

  // Custom link icons (business portal)
  Gift,
  Instagram,
  ExternalLink,
  Globe,
  ShoppingCart,
  MapPin,
  Phone,
  Mail,
  Star,
  Link,
  Share2,
  Ticket,
  Calendar,
  Clock,
  Camera,
  MessageCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Curated map of Lucide icons used by the restaurant API.
 *
 * Covers icons from:
 * - KeywordOption.icon (beverages, dietary, views, ambiance, entertainment, parking, menu)
 * - OutletToggleOption.icon (service, menu_times, family, tech_payment, accessibility, pricing)
 * - Vibe icon maps (vibes computed on the backend)
 * - Custom link icons from the business portal
 *
 * Keys are PascalCase Lucide icon names as stored in the database.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  // Fallback
  Sparkles,

  // Beverages & drinks
  Beer,
  Wine,
  Martini,
  Coffee,
  CupSoda,
  GlassWater,
  IceCream,

  // Food & dining
  UtensilsCrossed,
  Utensils,
  ChefHat,
  Salad,
  Leaf,

  // Nature & views
  TreePine,
  Mountain,
  Sun,
  Sunrise,
  Moon,
  Eye,

  // People & social
  Users,
  Baby,
  Dog,
  Heart,

  // Entertainment
  Music,
  Trophy,
  Tv,
  Gamepad2,
  Sofa,

  // Transport & parking
  Car,
  Truck,
  CircleParking,

  // Business & tech
  Briefcase,
  Zap,
  Package,
  CalendarCheck,
  Smartphone,
  Wallet,
  CreditCard,
  Banknote,
  Accessibility,

  // Vibes
  PartyPopper,

  // Custom link icons (business portal)
  Gift,
  Instagram,
  ExternalLink,
  Globe,
  ShoppingCart,
  MapPin,
  Phone,
  Mail,
  Star,
  Link,
  Share2,
  Ticket,
  Calendar,
  Clock,
  Camera,
  MessageCircle,
};

/**
 * Resolve a Lucide icon name string (from the API) to its component.
 * Falls back to Sparkles if the name is not found or not provided.
 *
 * @param iconName - The PascalCase Lucide icon name (e.g., 'Wine', 'Music')
 * @returns The corresponding LucideIcon component
 *
 * @example
 * const Icon = getIconComponent('Wine'); // Wine icon component
 * <Icon size={14} />
 */
export function getIconComponent(iconName: string | null | undefined): LucideIcon {
  if (!iconName) return Sparkles;

  return ICON_MAP[iconName] ?? Sparkles;
}
